import React, { useState } from 'react';
import { webSocketManager } from '../../services/websocket';
import { WebSocketConnection } from '../../types';
import { Wifi, WifiOff, Activity, AlertTriangle } from 'lucide-react';

interface WebSocketStatusProps {
  showDetails?: boolean;
  className?: string;
}

export function WebSocketStatus({ showDetails = false, className = '' }: WebSocketStatusProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [status] = useState(() => webSocketManager.getOverallStatus());

  const getStatusIcon = () => {
    if (status.connected === status.total && status.total > 0) {
      return <Wifi className="w-4 h-4 text-green-500" />;
    } else if (status.connected > 0) {
      return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
    } else {
      return <WifiOff className="w-4 h-4 text-red-500" />;
    }
  };

  const getStatusColor = () => {
    if (status.connected === status.total && status.total > 0) {
      return 'text-green-600 dark:text-green-400';
    } else if (status.connected > 0) {
      return 'text-yellow-600 dark:text-yellow-400';
    } else {
      return 'text-red-600 dark:text-red-400';
    }
  };

  const getStatusText = () => {
    if (status.total === 0) {
      return 'No connections';
    } else if (status.connected === status.total) {
      return 'All connected';
    } else if (status.connected > 0) {
      return `${status.connected}/${status.total} connected`;
    } else {
      return 'Disconnected';
    }
  };

  const formatConnectionStatus = (conn: WebSocketConnection): string => {
    if (conn.isConnected) {
      return 'Connected';
    } else if (conn.reconnectAttempts > 0) {
      return `Reconnecting (${conn.reconnectAttempts}/${conn.maxReconnectAttempts})`;
    } else {
      return 'Disconnected';
    }
  };

  if (!showDetails) {
    return (
      <div className={`flex items-center space-x-2 ${className}`}>
        {getStatusIcon()}
        <span className={`text-sm ${getStatusColor()}`}>
          {getStatusText()}
        </span>
      </div>
    );
  }

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg shadow ${className}`}>
      <div 
        className="p-4 cursor-pointer flex items-center justify-between"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center space-x-3">
          {getStatusIcon()}
          <div>
            <h3 className="text-sm font-medium text-gray-900 dark:text-white">
              WebSocket Status
            </h3>
            <p className={`text-xs ${getStatusColor()}`}>
              {getStatusText()}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <Activity className="w-4 h-4 text-gray-400" />
          <svg
            className={`w-4 h-4 text-gray-400 transform transition-transform ${
              isExpanded ? 'rotate-180' : ''
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {isExpanded && (
        <div className="border-t border-gray-200 dark:border-gray-700 p-4">
          <div className="space-y-3">
            {Object.entries(status.services).map(([channel, connection]) => (
              <div key={channel} className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  {connection.isConnected ? (
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  ) : (
                    <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                  )}
                  <span className="text-sm text-gray-900 dark:text-white capitalize">
                    {channel}
                  </span>
                </div>
                <div className="text-right">
                  <div className={`text-xs ${
                    connection.isConnected ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                  }`}>
                    {formatConnectionStatus(connection)}
                  </div>
                  {connection.lastConnected && (
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Last: {new Date(connection.lastConnected).toLocaleTimeString()}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {status.total === 0 && (
            <div className="text-center py-4 text-gray-500 dark:text-gray-400 text-sm">
              No WebSocket connections active
            </div>
          )}

          <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
              <span>Real-time updates</span>
              <div className="flex items-center space-x-1">
                <div className={`w-2 h-2 rounded-full ${
                  status.connected > 0 ? 'bg-green-500 animate-pulse' : 'bg-gray-400'
                }`}></div>
                <span>{status.connected > 0 ? 'Active' : 'Inactive'}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}